import {
  fetchApiGetV2,
  fetchApiPostV2,
  fetchApiPutV2,
} from "../../../../api/api";
import { updateColors, addNewColor } from "./colorActions";

/**
 * Website color from builder API v2 `GET/PUT /colors` (camelCase; no timestamps or website uuid).
 * Backend: `GET/POST/PUT /colors`, `DELETE /colors/{colorId}` (delete not used in this app).
 */
export interface Color {
  id: number;
  name: string;
  color: string;
  sortOrder: number;
}

/** `POST /colors` body */
export interface ColorCreateBody {
  name: string;
  color: string;
  sortOrder?: number;
}

/** One row in `PUT /colors` body (omit `id` for new colors in bulk save). */
export interface ColorPutBody {
  id?: number;
  name: string;
  color: string;
  sortOrder: number;
}

/** Values passed from forms into {@link saveColors} (may omit `id` for new rows). */
export type ColorSaveInput = {
  id?: number | string;
  name: string;
  color: string;
  sortOrder?: number;
};

function normalizeColorFromApi(raw: unknown): Color {
  const r = raw as Record<string, unknown>;
  return {
    id: Number(r.id),
    name: String(r.name ?? ""),
    color: String(r.color ?? ""),
    sortOrder: Number(r.sortOrder ?? 0),
  };
}

function normalizeColorsFromApi(list: unknown): Color[] {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map(normalizeColorFromApi);
}

function toColorPutBody(row: ColorSaveInput): ColorPutBody {
  const idRaw = row.id;
  let id: number | undefined;
  if (typeof idRaw === "number" && Number.isFinite(idRaw)) {
    id = idRaw;
  } else if (typeof idRaw === "string" && /^\d+$/.test(idRaw)) {
    id = Number(idRaw);
  }

  const sortOrder = Number(row.sortOrder ?? 1);

  const body: ColorPutBody = {
    name: row.name,
    color: row.color,
    sortOrder: Number.isFinite(sortOrder) ? sortOrder : 1,
  };
  if (id !== undefined) {
    body.id = id;
  }
  return body;
}

const ColorAPI = {
  loadColors,
  addColor,
  saveColors,
};
export default ColorAPI;

export function loadColors() {
  return fetchApiGetV2<unknown>(`/colors`, {}).then((res) =>
    updateColors(normalizeColorsFromApi(res))
  );
}

export function addColor(data: ColorCreateBody) {
  const body: ColorCreateBody = {
    name: data.name,
    color: data.color,
    ...(data.sortOrder != null ? { sortOrder: data.sortOrder } : {}),
  };
  return fetchApiPostV2<unknown>(`/colors`, body).then((d) =>
    addNewColor(normalizeColorFromApi(d))
  );
}

export function saveColors(data: ColorSaveInput[]) {
  const payload = data.map(toColorPutBody);
  return fetchApiPutV2<unknown>(`/colors`, payload).then((d) =>
    updateColors(normalizeColorsFromApi(d))
  );
}
